"use client";

import { useDict } from "@/components/i18n/DictProvider";
import { Lock } from "lucide-react";
import { ImageFrame } from "@/components/ui/ImageFrame";
import { DynamicVideoPlayer as VideoPlayer } from "@/components/player/DynamicVideoPlayer";
import { type Lang } from "@/lib/i18n";
import type { ModuleDetail, OttVideo } from "@/lib/lecturehub/types";
import { videoBoxStyle, videoStreamUrl } from "@/lib/lecturehub/video";
import { buildImageUrl } from "@/lib/lecturehub/images";

/**
 * The OTT "stage": the video frame (the source's own shape), and nothing else. Title + meta live in
 * OttHero; the access gate lives once, in the rail (OttDetailClient); the body
 * copy lives once, in the "about" section below the stage. Nothing textual is
 * repeated here — the frame carries only media.
 *
 * The video on stage is the module's own (`ott.video`) or, for a series, the
 * SELECTED episode's (`episode`, decision D32) — the same frame, the same
 * priorities, one player. Whether it plays is the video's own stream field:
 * the backend fills it per file (09-gotchas rule 3), so it is never derived
 * from the module's `canView`, which only paints the lock badge.
 *
 * Content priority:
 *   playable video      → the player
 *   still transcoding   → a "processing" placeholder
 *   locked + a trailer  → the trailer, labelled (the rail carries the CTA)
 *   otherwise           → the poster (with a lock badge when locked)
 */
export function OttView({
  module: m,
  lang,
  cfHash = null,
  episode,
  autoplay = false,
  onEnded,
}: {
  module: ModuleDetail;
  lang: Lang;
  /** Portal.cloudflareAccountHash — resolves posters to CF delivery URLs. */
  cfHash?: string | null;
  /** A series' selected episode; `undefined` for a single video, `null` for a
   *  series with nothing to select (no episodes). */
  episode?: OttVideo | null;
  /** Start the selected episode at once — after the viewer's own choice only. */
  autoplay?: boolean;
  /** The episode played to its end (a series advances). */
  onEnded?: () => void;
}) {
  const t = useDict();
  const ott = m.ott;
  const series = episode !== undefined;
  const video = series ? (episode?.video ?? null) : (ott?.video ?? null);
  // Every poster resolves through buildImageUrl (CF variant when the tenant has
  // Cloudflare Images, plain `uri` otherwise) — never a raw `uri`, which on the
  // local backend is an http://api.lh.local address no browser can load. An
  // episode's own art first, then the video's, then the module's.
  const playerPoster = buildImageUrl(
    (series ? episode?.image : null) ?? video?.image ?? ott?.image ?? null,
    "xl",
    cfHash,
  );
  const trailerPoster = buildImageUrl(ott?.trailer?.image ?? ott?.image ?? null, "xl", cfHash);
  // The still frame prefers the 16:9 poster (`image`) — that IS the frame's
  // aspect — and falls back to the wide hero, which the band above already
  // shows, only when there is no poster at all.
  const framePoster = buildImageUrl(
    ott?.image?.uri ? ott.image : (ott?.heroImage ?? null),
    "xl",
    cfHash,
  );

  const mainStream = videoStreamUrl(video);
  const trailerStream = videoStreamUrl(ott?.trailer);
  const canView = m.canView !== false;
  const videoStillProcessing = !!video && video.processed === false;
  // No stream on a video that exists = the gate said no for this caller. A
  // filled stream is always playable: the backend fills it only for a caller
  // who may watch (rule 3, OR across attachments) — `canView` is not consulted.
  const videoBlockedByAccess = !!video && !mainStream;
  const locked = !canView || videoBlockedByAccess;

  let frame: React.ReactNode;
  if (video && mainStream && !videoStillProcessing) {
    frame = (
      <div className="overflow-hidden rounded-2xl bg-near-black shadow-soft">
        <VideoPlayer
          src={mainStream}
          poster={playerPoster}
          width={video.width}
          height={video.height}
          subtitles={video.subtitles}
          chapters={video.chapters}
          duration={video.duration}
          autoplay={autoplay}
          onEnded={onEnded}
          lang={lang}
        />
      </div>
    );
  } else if (videoStillProcessing) {
    frame = (
      <div
        style={videoBoxStyle(video)}
        className="grid w-full place-items-center overflow-hidden rounded-2xl bg-near-black text-white"
      >
        <p>{t.detail.processing}</p>
      </div>
    );
  } else if (locked && trailerStream) {
    // No standalone "Trailer" label: the rail gate already says the full
    // content is locked, and the player is the frame's whole story.
    frame = (
      <div className="overflow-hidden rounded-2xl bg-near-black shadow-soft">
        <VideoPlayer
          src={trailerStream}
          poster={trailerPoster}
          width={ott?.trailer?.width}
          height={ott?.trailer?.height}
          subtitles={ott?.trailer?.subtitles}
          lang={lang}
        />
      </div>
    );
  } else {
    frame = (
      <div className="relative overflow-hidden rounded-2xl bg-near-black shadow-soft">
        {framePoster ? (
          // `priority`: this frame IS the watch page's LCP element, and the
          // default lazy <img> never loads here — the page streams in under the
          // route's loading skeleton (a hidden Suspense chunk), and Chrome
          // does not schedule lazy images that were hidden at parse time.
          <ImageFrame src={framePoster} aspect="16/9" priority />
        ) : (
          // No poster → the brand gradient tile the hero and episode rows also
          // fall back to, not ImageFrame's grey "No image" box: a flat grey slab
          // under a lock reads as a broken image, not a locked one.
          <div
            aria-hidden
            className="aspect-video w-full bg-gradient-to-br from-teal to-foreground"
          />
        )}
        {locked ? (
          // Vignette (not a flat wash) keeps the poster legible at the edges and
          // pulls the eye to the disc; the disc is frosted glass over whatever
          // sits behind it.
          <span
            aria-hidden
            className="absolute inset-0 grid place-items-center bg-[radial-gradient(ellipse_at_center,rgb(0_0_0/0.2),rgb(0_0_0/0.5))]"
          >
            <span className="grid size-16 place-items-center rounded-full bg-white/12 text-white shadow-[0_8px_24px_-8px_rgb(0_0_0/0.6)] ring-1 ring-inset ring-white/35 backdrop-blur-md">
              <Lock className="size-6" strokeWidth={2} />
            </span>
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="min-w-0" id="player">
      {frame}
    </div>
  );
}
